/*
================================================================================
FILE HEADER — apps/studio-web/src/lib/settings.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Settings page            | Created — localStorage|
|    |            |                   |                          | backed settings with |
|    |            |                   |                          | typed defaults       |
| 2  | 2026-07-02 | Claude Sonnet 4.6 | Tier-2 Ollama (Phase 3)  | Added tier2Enabled,  |
|    |            |                   |                          | ollamaEndpoint,      |
|    |            |                   |                          | ollamaModel fields   |
| 3  | 2026-07-02 | Claude Sonnet 5   | Phase 6 AI Assist mode   | Added aiAssistEnabled|
|    |            |                   |                          | (default true — Lane |
|    |            |                   |                          | 0-1 are pure-local)  |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/components/SettingsPage.tsx
- apps/studio-web/src/App.tsx  (reads defaults for RunConfigSheet)

EXPORTS COVERED
---------------
- PlexSettings     — full settings shape
- DEFAULT_SETTINGS — factory defaults (safe to spread over partial saved data)
- loadSettings()   — reads from localStorage, merges with defaults
- saveSettings()   — writes to localStorage
- useSettings()    — React hook: returns [settings, save] with reactivity

IMPORTANT POINTS
----------------
1. Key = 'plex-settings-v1'. Version suffix allows breaking changes without
   corrupting old data — just bump to v2 and supply new defaults.
2. loadSettings() always merges with DEFAULT_SETTINGS so new fields added in
   later versions have their defaults automatically.
3. The `theme` field is reserved. Dark is currently the only supported theme.
4. screenshotOnFailure / screenshotEveryStep are global toggles that default
   the per-run RunConfigSheet values. Per-run can still override them.
================================================================================
*/

import { useState, useCallback } from 'react';

const STORAGE_KEY = 'plex-settings-v1';

export interface PlexSettings {
  // Profile
  userName:    string;

  // Project config
  projectName: string;
  baseUrl:     string;

  // Execution defaults (pre-populate RunConfigSheet)
  defaultBrowser:            'chromium' | 'firefox' | 'webkit';
  defaultHeadless:           boolean;
  defaultStepDelayMs:        number;
  defaultStepTimeoutMs:      number;
  defaultStopOnFirstFailure: boolean;

  // Screenshots
  screenshotOnFailure:  boolean;
  screenshotEveryStep:  boolean;

  // CLI relay
  studioRelayPort: number;

  // Tier-2 Local AI (Ollama)
  tier2Enabled:   boolean;
  ollamaEndpoint: string;
  ollamaModel:    string;  // e.g. "qwen2.5vl:7b" or "qwen2.5vl:3b" (Ollama library name has no hyphen before "vl")

  // AI Assist mode (Phase 6) — Lane 0 grammar + Lane 1 ONNX resolution, zero tokens
  aiAssistEnabled: boolean;

  // UI
  theme: 'dark'; // Light theme is Phase N; value is reserved
}

export const DEFAULT_SETTINGS: PlexSettings = {
  userName:    '',
  projectName: '',
  baseUrl:     '',

  defaultBrowser:            'chromium',
  defaultHeadless:           false,
  defaultStepDelayMs:        300,
  defaultStepTimeoutMs:      6000,
  defaultStopOnFirstFailure: true,

  screenshotOnFailure: false,
  screenshotEveryStep: false,

  studioRelayPort: 7700,

  tier2Enabled:   false,
  ollamaEndpoint: 'http://localhost:11434',
  ollamaModel:    'qwen2.5vl:7b',

  aiAssistEnabled: true,

  theme: 'dark',
};

export function loadSettings(): PlexSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) as Partial<PlexSettings> };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: PlexSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch { /* storage full or blocked */ }
}

/** React hook: returns [settings, save]. Re-renders the component when save() is called. */
export function useSettings(): [PlexSettings, (patch: Partial<PlexSettings>) => void] {
  const [settings, setSettings] = useState<PlexSettings>(loadSettings);

  const save = useCallback((patch: Partial<PlexSettings>) => {
    setSettings(prev => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  return [settings, save];
}
